import React from "react";
import { TOOLS, SHAPES, HITBOX_PADDING, RFI_TAG_WIDTH, RFI_TAG_HEIGHT, CONNECTION_INNER_RECT_INSET, TEXT_ANNOTATION_CONTENT_INSET } from "../constants";
import { getCloudPath, getCloudSegments, getTextAnnotationMetrics, getCalloutBox, normalizeCloudSegment, isDrawingLink, isAutoGeneratedDrawingLink, getCalloutLinkColor, getSheetLinkTooltip, getSheetLinkHitRadius } from "../utils";
import { annotationHasLinkedPhoto, getLinkedPhotoDisplayUrl } from "../utils/linkedPhoto";
import CalloutLeader, { CalloutResizeHandles } from "./CalloutLeader";

const AnnotationRenderer = ({
  annotations,
  pageIndex,
  selectedAnnotationIndex,
  editingTextAnnotationId = null,
  showConnectionBox,
  cloudDrawingActive,
  currentTool,
  currentShape,
  isDrawing,
  startPoint,
  onAnnotationClick,
  onAnnotationDoubleClick,
  onStartDrawing,
  onEndDrawing,
  onTagClick,
  onTagClose,
  onCalloutKneeReset,
  visualZoom = 1,
}) => {
  const getUserName = (annotation) => {
    if (!annotation.user) return "Added by: Unknown";
    const name = annotation.user.name?.trim();
    if (name) return `Added by: ${name}`;
    const displayName = annotation.user.email || annotation.user.username || "Unknown";
    return `Added by: ${displayName}`;
  };

  const createCloudHandler = (annotation, actualIndex, e) => {
    if (
      cloudDrawingActive &&
      currentTool === TOOLS.SHAPE &&
      currentShape === SHAPES.CLOUD
    ) {
      if (!e.target.getAttribute("data-id")) {
        if (isDrawing && startPoint) {
          onEndDrawing(e);
        }
        if (onStartDrawing(e)) {
          e.stopPropagation();
          return;
        }
        return;
      }
    }
    if (onStartDrawing(e)) {
      e.stopPropagation();
    }
  };

  return (
    <>
      {annotations
        .filter((ann) => ann.page === pageIndex)
        .map((annotation, filteredIndex) => {
          const actualIndex = annotations.findIndex((ann) => ann.id === annotation.id);
          const selectedAnnotation =
            selectedAnnotationIndex !== null
              ? annotations[selectedAnnotationIndex]
              : null;
          const isSelected =
            actualIndex === selectedAnnotationIndex ||
            (!!selectedAnnotation?.cloudGroupId &&
              annotation.cloudGroupId === selectedAnnotation.cloudGroupId);
          const commonProps = {
            "data-id": annotation.id,
            onClick: (e) => onAnnotationClick(actualIndex, e),
            style: {
              cursor: "pointer",
              filter: isSelected ? "brightness(1.2)" : "none",
            },
          };

          if (annotation.type === "pen") {
            return (
              <g key={annotation.id}>
                <title>{getUserName(annotation)}</title>
                <path
                  d={annotation.path}
                  stroke="transparent"
                  strokeWidth={annotation.strokeWidth + HITBOX_PADDING}
                  fill="none"
                  onMouseDown={(e) => createCloudHandler(annotation, actualIndex, e)}
                  data-id={annotation.id}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => onAnnotationClick(actualIndex, e)}
                />
                <path
                  {...commonProps}
                  d={annotation.path}
                  stroke={annotation.color}
                  strokeWidth={isSelected ? annotation.strokeWidth * 1.2 : annotation.strokeWidth}
                  fill="none"
                />
              </g>
            );
          }

          if (annotation.type === "text") {
            // While the inline editor is open for this annotation, hide the SVG
            // text so it doesn't steal the opening click / show a double image.
            if (editingTextAnnotationId && annotation.id === editingTextAnnotationId) {
              return null;
            }
            const fontSize = annotation.fontSize || 16;
            // Pass through undefined boxWidth so legacy annotations keep content-sized layout.
            const { lines, width, height, lineHeight } = getTextAnnotationMetrics(
              annotation.content,
              fontSize,
              annotation.boxWidth,
              annotation.boxHeight,
            );
            const boxLeft = annotation.position.x - width / 2;
            const boxTop = annotation.position.y - height / 2;
            // Match TextAnnotationInput padding so commit doesn't shift glyphs horizontally.
            const textX = boxLeft + TEXT_ANNOTATION_CONTENT_INSET;
            const firstLineY = boxTop + lineHeight / 2 + 2;
            const calloutBox = annotation.calloutTip ? getCalloutBox(annotation) : null;
            const calloutHitProps = (part) => ({
              "data-id": annotation.id,
              "data-callout-part": part,
              style: { cursor: part === "leader" ? "move" : "crosshair" },
              onMouseDown: (e) => {
                e.preventDefault();
                createCloudHandler(annotation, actualIndex, e);
              },
              onClick: (e) => onAnnotationClick(actualIndex, e),
            });
            return (
              <g key={annotation.id}>
                <title>{getUserName(annotation)}</title>
                {calloutBox && (
                  <CalloutLeader
                    tip={annotation.calloutTip}
                    knee={annotation.calloutKnee}
                    box={calloutBox}
                    color={annotation.color}
                    showHandles={isSelected}
                    leaderHitProps={calloutHitProps("leader")}
                    tipHitProps={calloutHitProps("tip")}
                    kneeHitProps={{
                      ...calloutHitProps("knee"),
                      onDoubleClick: (e) => {
                        e.stopPropagation();
                        onCalloutKneeReset?.(actualIndex);
                      },
                    }}
                  />
                )}
                <rect
                  x={calloutBox ? calloutBox.left : boxLeft}
                  y={calloutBox ? calloutBox.top : boxTop}
                  width={calloutBox ? calloutBox.width : width}
                  height={calloutBox ? calloutBox.height : height}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    createCloudHandler(annotation, actualIndex, e);
                  }}
                  onClick={(e) => onAnnotationClick(actualIndex, e)}
                  onDoubleClick={(e) => onAnnotationDoubleClick(actualIndex, e)}
                  data-id={annotation.id}
                />
                <text
                  {...commonProps}
                  x={textX}
                  y={firstLineY}
                  fill={annotation.color}
                  fontSize={fontSize}
                  textAnchor="start"
                  dominantBaseline="middle"
                  fontWeight={annotation.style?.fontWeight}
                  fontStyle={annotation.style?.fontStyle}
                  textDecoration={annotation.style?.textDecoration}
                  style={{
                    background: annotation.style?.highlight ? "#ffe066" : "none",
                    filter: isSelected ? "brightness(1.2)" : "none",
                    cursor: "pointer",
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    createCloudHandler(annotation, actualIndex, e);
                  }}
                  onDoubleClick={(e) => onAnnotationDoubleClick(actualIndex, e)}
                >
                  {lines.map((line, lineIndex) => (
                    <tspan
                      key={`${annotation.id}-line-${lineIndex}`}
                      x={textX}
                      dy={lineIndex === 0 ? 0 : lineHeight}
                      data-id={annotation.id}
                    >
                      {line || "\u00A0"}
                    </tspan>
                  ))}
                </text>
                {annotation.style?.squiggly && (
                  <path
                    d={`M${boxLeft},${
                      annotation.position.y + height / 2 - lineHeight / 4
                    } Q${annotation.position.x - width / 4},${
                      annotation.position.y + height / 2
                    } ${annotation.position.x},${
                      annotation.position.y + height / 2 - lineHeight / 4
                    } Q${annotation.position.x + width / 4},${
                      annotation.position.y + height / 2 - lineHeight / 2
                    } ${annotation.position.x + width / 2},${
                      annotation.position.y + height / 2 - lineHeight / 4
                    }`}
                    stroke="red"
                    strokeWidth={2}
                    fill="none"
                  />
                )}
                {calloutBox && isSelected && (
                  <CalloutResizeHandles
                    box={calloutBox}
                    getHandleProps={(handle) => calloutHitProps(`resize-${handle}`)}
                  />
                )}
              </g>
            );
          }

          if (annotation.type === "shape") {
            switch (annotation.shapeType) {
              case SHAPES.LINE:
                return (
                  <g key={annotation.id}>
                    <title>{getUserName(annotation)}</title>
                    <line
                      x1={annotation.start.x}
                      y1={annotation.start.y}
                      x2={annotation.end.x}
                      y2={annotation.end.y}
                      stroke="transparent"
                      strokeWidth={annotation.strokeWidth + HITBOX_PADDING}
                      fill="none"
                      onMouseDown={(e) => createCloudHandler(annotation, actualIndex, e)}
                      data-id={annotation.id}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => onAnnotationClick(actualIndex, e)}
                    />
                    <line
                      {...commonProps}
                      x1={annotation.start.x}
                      y1={annotation.start.y}
                      x2={annotation.end.x}
                      y2={annotation.end.y}
                      stroke={annotation.color}
                      strokeWidth={isSelected ? annotation.strokeWidth * 1.2 : annotation.strokeWidth}
                    />
                  </g>
                );
              case SHAPES.RECTANGLE:
                return (
                  <g key={annotation.id}>
                    <title>{getUserName(annotation)}</title>
                    <rect
                      x={Math.min(annotation.start.x, annotation.end.x)}
                      y={Math.min(annotation.start.y, annotation.end.y)}
                      width={Math.abs(annotation.end.x - annotation.start.x)}
                      height={Math.abs(annotation.end.y - annotation.start.y)}
                      stroke="transparent"
                      strokeWidth={annotation.strokeWidth + HITBOX_PADDING}
                      fill="none"
                      onMouseDown={(e) => createCloudHandler(annotation, actualIndex, e)}
                      data-id={annotation.id}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => onAnnotationClick(actualIndex, e)}
                    />
                    <rect
                      {...commonProps}
                      x={Math.min(annotation.start.x, annotation.end.x)}
                      y={Math.min(annotation.start.y, annotation.end.y)}
                      width={Math.abs(annotation.end.x - annotation.start.x)}
                      height={Math.abs(annotation.end.y - annotation.start.y)}
                      stroke={annotation.color}
                      strokeWidth={isSelected ? annotation.strokeWidth * 1.2 : annotation.strokeWidth}
                      fill="none"
                    />
                  </g>
                );
              case SHAPES.CIRCLE: {
                const left = Math.min(annotation.start.x, annotation.end.x);
                const top = Math.min(annotation.start.y, annotation.end.y);
                const width = Math.abs(annotation.end.x - annotation.start.x);
                const height = Math.abs(annotation.end.y - annotation.start.y);
                return (
                  <g key={annotation.id}>
                    <title>{getUserName(annotation)}</title>
                    <ellipse
                      cx={left + width / 2}
                      cy={top + height / 2}
                      rx={width / 2}
                      ry={height / 2}
                      stroke="transparent"
                      strokeWidth={annotation.strokeWidth + HITBOX_PADDING}
                      fill="none"
                      onMouseDown={(e) => createCloudHandler(annotation, actualIndex, e)}
                      data-id={annotation.id}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => onAnnotationClick(actualIndex, e)}
                    />
                    <ellipse
                      {...commonProps}
                      cx={left + width / 2}
                      cy={top + height / 2}
                      rx={width / 2}
                      ry={height / 2}
                      stroke={annotation.color}
                      strokeWidth={annotation.strokeWidth}
                      fill="none"
                    />
                  </g>
                );
              }
              case SHAPES.POLYGON: {
                const midX = (annotation.start.x + annotation.end.x) / 2;
                return (
                  <g key={annotation.id}>
                    <title>{getUserName(annotation)}</title>
                    <polygon
                      points={`${annotation.start.x},${annotation.end.y} ${midX},${annotation.start.y} ${annotation.end.x},${annotation.end.y}`}
                      stroke="transparent"
                      strokeWidth={annotation.strokeWidth + HITBOX_PADDING}
                      fill="none"
                      onMouseDown={(e) => createCloudHandler(annotation, actualIndex, e)}
                      data-id={annotation.id}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => onAnnotationClick(actualIndex, e)}
                    />
                    <polygon
                      {...commonProps}
                      points={`${annotation.start.x},${annotation.end.y} ${midX},${annotation.start.y} ${annotation.end.x},${annotation.end.y}`}
                      stroke={annotation.color}
                      strokeWidth={isSelected ? annotation.strokeWidth * 1.2 : annotation.strokeWidth}
                      fill="none"
                    />
                  </g>
                );
              }
              case SHAPES.CLOUD: {
                const segments = getCloudSegments(annotation);
                return (
                  <g key={annotation.id}>
                    <title>{getUserName(annotation)}</title>
                    {segments.map((segment, segmentIndex) => {
                      const [x1, y1, x2, y2] = normalizeCloudSegment(segment);
                      const cloudD = getCloudPath(x1, y1, x2, y2, 5, 8);
                      return (
                        <g key={`${annotation.id}-${segmentIndex}`}>
                          <path
                            d={cloudD}
                            stroke="transparent"
                            strokeWidth={annotation.strokeWidth + HITBOX_PADDING}
                            fill="none"
                            onMouseDown={(e) =>
                              createCloudHandler(annotation, actualIndex, e)
                            }
                            data-id={annotation.id}
                            style={{ cursor: "pointer" }}
                            onClick={(e) => onAnnotationClick(actualIndex, e)}
                          />
                          <path
                            {...commonProps}
                            d={cloudD}
                            stroke={annotation.color}
                            strokeWidth={
                              isSelected
                                ? annotation.strokeWidth * 1.2
                                : annotation.strokeWidth
                            }
                            fill="none"
                          />
                        </g>
                      );
                    })}
                  </g>
                );
              }
              case SHAPES.ARROW: {
                const angle = Math.atan2(
                  annotation.end.y - annotation.start.y,
                  annotation.end.x - annotation.start.x
                );
                const headlen = 15;
                const arrowX = annotation.end.x - headlen * Math.cos(angle - Math.PI / 6);
                const arrowY = annotation.end.y - headlen * Math.sin(angle - Math.PI / 6);
                const arrowX2 = annotation.end.x - headlen * Math.cos(angle + Math.PI / 6);
                const arrowY2 = annotation.end.y - headlen * Math.sin(angle + Math.PI / 6);
                return (
                  <g key={annotation.id}>
                    <title>{getUserName(annotation)}</title>
                    <line
                      x1={annotation.start.x}
                      y1={annotation.start.y}
                      x2={annotation.end.x}
                      y2={annotation.end.y}
                      stroke="transparent"
                      strokeWidth={annotation.strokeWidth + HITBOX_PADDING}
                      onMouseDown={(e) => createCloudHandler(annotation, actualIndex, e)}
                      data-id={annotation.id}
                      style={{ cursor: "pointer" }}
                    />
                    <line
                      {...commonProps}
                      x1={annotation.start.x}
                      y1={annotation.start.y}
                      x2={annotation.end.x}
                      y2={annotation.end.y}
                      stroke={annotation.color}
                      strokeWidth={isSelected ? annotation.strokeWidth * 1.2 : annotation.strokeWidth}
                    />
                    <polygon
                      points={`${annotation.end.x},${annotation.end.y} ${arrowX},${arrowY} ${arrowX2},${arrowY2}`}
                      fill={annotation.color}
                    />
                  </g>
                );
              }
              default:
                return null;
            }
          }

          if (annotation.type === "highlight") {
            return (
              <g key={annotation.id}>
                <title>{getUserName(annotation)}</title>
                <rect
                  {...commonProps}
                  x={Math.min(annotation.start.x, annotation.end.x)}
                  y={Math.min(annotation.start.y, annotation.end.y)}
                  width={Math.abs(annotation.end.x - annotation.start.x)}
                  height={Math.abs(annotation.end.y - annotation.start.y)}
                  fill="#ffe066"
                  fillOpacity={0.6}
                  stroke="none"
                />
              </g>
            );
          }

          if (annotation.type === "squiggly") {
            const x1 = annotation.start.x;
            const x2 = annotation.end.x;
            const y = annotation.start.y;
            const length = Math.abs(x2 - x1);
            const step = 8;
            let d = `M${x1},${y}`;
            for (let i = 0; i < length; i += step) {
              const x = x1 + i;
              const yOffset = i % (2 * step) === 0 ? 4 : -4;
              d += ` Q${x + step / 2},${y + yOffset} ${x + step},${y}`;
            }
            return (
              <g key={annotation.id}>
                <title>{getUserName(annotation)}</title>
                <path
                  {...commonProps}
                  d={d}
                  stroke="red"
                  strokeWidth={2}
                  fill="none"
                />
              </g>
            );
          }

          if (annotation.type === "connection") {
            if (showConnectionBox && isSelected && selectedAnnotationIndex === actualIndex) {
              return null;
            }
            const boxX = Math.min(annotation.start.x, annotation.end.x);
            const boxY = Math.min(annotation.start.y, annotation.end.y);
            const boxWidth = Math.abs(annotation.end.x - annotation.start.x);
            const boxHeight = Math.abs(annotation.end.y - annotation.start.y);
            
            // RFI tag dimensions and position (bottom right corner of inner rectangle)
            const tagWidth = RFI_TAG_WIDTH;
            const tagHeight = RFI_TAG_HEIGHT;
            const innerRectInset = CONNECTION_INNER_RECT_INSET;
            const tagX = boxX + boxWidth - innerRectInset;
            const tagY = boxY + boxHeight - innerRectInset - tagHeight;
            const hasRfi = annotation.linkedRfiId && annotation.rfiTag;
            const hasPhoto = annotationHasLinkedPhoto(annotation);
            const hasDrawing = isDrawingLink(annotation);
            const isOcrDrawingLink = isAutoGeneratedDrawingLink(annotation);
            const frameInset = isOcrDrawingLink ? 0 : CONNECTION_INNER_RECT_INSET;
            const drawingLabel = annotation.targetDrawingNumber || "Sheet";
            const hasLink = hasRfi || hasPhoto || hasDrawing;

            if (isOcrDrawingLink) {
              const calloutColor = getCalloutLinkColor(annotation);
              const calloutCx = boxX + boxWidth / 2;
              const calloutCy = boxY + boxHeight / 2;
              return (
                <g key={annotation.id}>
                  <title>{getSheetLinkTooltip(annotation)}</title>
                  <ellipse
                    cx={calloutCx}
                    cy={calloutCy}
                    rx={Math.max(boxWidth / 2, 6)}
                    ry={Math.max(boxHeight / 2, 6)}
                    fill={`${calloutColor}22`}
                    stroke={calloutColor}
                    strokeWidth={1.1}
                    style={{ pointerEvents: "none" }}
                  />
                  <circle
                    cx={calloutCx}
                    cy={calloutCy}
                    r={getSheetLinkHitRadius(annotation, visualZoom)}
                    fill="transparent"
                    stroke="none"
                    style={{ cursor: "pointer", pointerEvents: "all" }}
                    onClick={(e) => onAnnotationClick(actualIndex, e)}
                    data-id={annotation.id}
                  />
                </g>
              );
            }

            return (
              <g key={annotation.id}>
                <title>{getUserName(annotation)}</title>
                <rect
                  x={boxX}
                  y={boxY}
                  width={boxWidth}
                  height={boxHeight}
                  fill="transparent"
                  stroke="none"
                  style={{ cursor: "pointer", pointerEvents: "all" }}
                  onClick={(e) => onAnnotationClick(actualIndex, e)}
                  data-id={annotation.id}
                />
                <rect
                  {...commonProps}
                  x={boxX + frameInset}
                  y={boxY + frameInset}
                  width={boxWidth - 2 * frameInset}
                  height={boxHeight - 2 * frameInset}
                  fill="none"
                  stroke="#ff4d4f"
                  strokeWidth={1.5}
                  style={{ pointerEvents: "none" }}
                />
                {/* RFI / photo tag at bottom right corner */}
                <rect
                  x={tagX}
                  y={tagY}
                  width={tagWidth}
                  height={tagHeight}
                  fill={hasLink ? "#ff4d4f" : "#e8e8e8"}
                  stroke={hasLink ? "none" : "#d9d9d9"}
                  strokeWidth={1}
                  rx={4}
                  style={{ cursor: hasLink ? "pointer" : "default", pointerEvents: "all" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasPhoto && onTagClick) {
                      onTagClick({
                        type: "photo",
                        linkedPhoto: annotation.linkedPhoto,
                        displayUrl: getLinkedPhotoDisplayUrl(annotation),
                        connectionId: annotation.id,
                      });
                    } else if (hasDrawing && onTagClick) {
                      onTagClick({ ...annotation, type: "drawing" });
                    } else if (hasRfi && onTagClick) {
                      onTagClick(annotation.rfiTag);
                    } else {
                      onAnnotationClick(actualIndex, e);
                    }
                  }}
                />
                <text
                  x={tagX + 6}
                  y={tagY + tagHeight / 2}
                  fill={hasLink ? "white" : "#999"}
                  fontSize={11}
                  fontWeight="bold"
                  textAnchor="start"
                  dominantBaseline="middle"
                  style={{ pointerEvents: "none", userSelect: "none" }}
                >
                  {hasPhoto
                    ? "Photo"
                    : hasDrawing
                    ? drawingLabel
                    : hasRfi
                    ? annotation.rfiTag.type === "submittal"
                      ? annotation.rfiTag.rfiNumber
                      : annotation.rfiTag.type === "punchlist"
                      ? `PL ${annotation.rfiTag.rfiNumber}`
                      : `RFI ${annotation.rfiTag.rfiNumber}`
                    : "No Link"}
                </text>
                {/* Close button for linked tag */}
                {hasLink && onTagClose && (
                  <>
                    <circle
                      cx={tagX + tagWidth + 6}
                      cy={tagY}
                      r={6}
                      fill="#ff4d4f"
                      style={{ cursor: "pointer", pointerEvents: "all" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasPhoto) {
                          onTagClose({ type: "photo", connectionId: annotation.id });
                        } else if (hasDrawing) {
                          onTagClose({ type: "drawing", connectionId: annotation.id });
                        } else {
                          onTagClose(annotation.rfiTag);
                        }
                      }}
                    />
                    <text
                      x={tagX + tagWidth + 6}
                      y={tagY}
                      fill="white"
                      fontSize={10}
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{ pointerEvents: "none", userSelect: "none" }}
                    >
                      ×
                    </text>
                  </>
                )}
              </g>
            );
          }

          return null;
        })}
    </>
  );
};

export default AnnotationRenderer;

